import React, { useEffect } from "react";
import { Modal, Row, Col, Typography, Divider, Table, Spin, Image, Empty } from "antd";
import { fetchBatchAvailability as fetchBatches } from "../../../store/commonThunks/lookups";
import { selectBatchAvailability } from "../../../store/commonSelectors/lookupsSelectors";
import { useDispatch, useSelector } from "react-redux";
import { selectAuthInfo } from "../../Auth/authSelectors";

const { Text, Title } = Typography;

const ViewItemModal = ({ visible, onClose, item }) => {
  const dispatch = useDispatch();
  const authInfo = useSelector(selectAuthInfo);
  const showBatches = authInfo?.enable_batch_number !== "no";

  const batches = useSelector(selectBatchAvailability);
  const { batchAvailability: loading } = useSelector((s) => s.lookups.loading);
  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const gstEnabled = authInfo?.gst_enabled === "yes";
  const showTax = !isIndia || gstEnabled;

  // 🔹 Fetch batch details when modal opens
  useEffect(() => {
    if (visible && item?.item_id && showBatches) {
      dispatch(fetchBatches(item.item_id));
    }
  }, [visible, item, dispatch, showBatches]);

  if (!item) return null;

  // ✅ Normalize yes/no fields
  const normalizedItem = {
    ...item,
    is_serial: item.is_serial === "yes",
    is_service: item.is_service === "yes",
  };

  const formatNumber = (val) => {
  if (val === null || val === undefined) return "0.00";
  const num = Number(val);
  return isNaN(num) ? "0.00" : num.toFixed(2);
};


const detailPairs = [
  ["Item ID", normalizedItem.item_id],
  ["Item Name", normalizedItem.item_name],
  ["Category Name", normalizedItem.category_name],
  ["Item Code", normalizedItem.item_code],
  ["Group Name", normalizedItem.group_name],
  ["Origin Name", normalizedItem.origin_name],
  ["Base Unit", normalizedItem.base_unit_name || "-"],
  ["Purchase Rate", formatNumber(normalizedItem.purchase_rate)],
  ["Sale Rate", formatNumber(normalizedItem.sale_rate)],
  ["Wholesaler Rate", formatNumber(normalizedItem.wholesaler_rate)],
  ["Corporate Rate", formatNumber(normalizedItem.corporate_rate)],
  ["Retailer Rate", formatNumber(normalizedItem.sale_rate)],
  ["Opening Qty", formatNumber(normalizedItem.opening_qty)],
  ["Opening Rate", formatNumber(normalizedItem.opening_rate)],
  ["Discount %", formatNumber(normalizedItem.discount_per)],
  ...(showTax ? [["Tax %", formatNumber(normalizedItem.tax_per)]] : []),
  ["Return Days", normalizedItem.return_days],
  ["Is Serial", normalizedItem.is_serial ? "Yes" : "No"],
  ["Is Service", normalizedItem.is_service ? "Yes" : "No"],
  ["Status", normalizedItem.status],
];

  const batchColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "Batch Number", dataIndex: "batch_name" },
    {
      title: "Quantity",
      dataIndex: "quantity",
      align: "right",
      render: (val) => {
        const num = Number(val) || 0; // convert null/undefined to 0
        return num.toFixed(2);
      },
    },
  ];

  return (
    <Modal
      title={<Title level={4}>{`${normalizedItem.item_name} Details`}</Title>}
      open={visible}
      onCancel={onClose}
      width={750}
      footer={null}
      centered
    >
      {/* 🖼️ Product Photo */}
      {normalizedItem.photo ? (
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <Image
            src={normalizedItem.photo}
            alt={normalizedItem.item_name}
            width={120}
            height={120}
            style={{
              borderRadius: 8,
              objectFit: "cover",
              border: "1px solid #f0f0f0",
            }}
            fallback={<Empty description="No Image" />}
          />
        </div>
      ) : (
        <div style={{ textAlign: "center", marginBottom: 20 }}>
          <Empty description="No Product Image Available" />
        </div>
      )}

      {/* 📋 Item Details */}
      <Row gutter={[16, 8]} style={{ marginBottom: 16 }}>
        {detailPairs.map(([label, value], i) => (
          <Col span={12} key={i}>
            <Text strong>{label}: </Text>
            <Text>{value ?? "-"}</Text>
          </Col>
        ))}
      </Row>

      {showBatches && (
        <>
          <Divider>BATCHES</Divider>

          {loading ? (
            <Spin style={{ display: "block", margin: "20px auto" }} />
          ) : batches?.length ? (
            <Table
              size="small"
              bordered
              columns={batchColumns}
              dataSource={batches}
              pagination={{ pageSize: 5, showSizeChanger: false }}
              rowKey={(r, i) => i}
            />
          ) : (
            <Empty description="No Batch Data" />
          )}
        </>
      )}
    </Modal>
  );
};

export default ViewItemModal;
